import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../lib/api/core';
// Spiegelt das Backend, damit die Vorschau-URL der oeffentlichen Route entspricht.
import { slugify } from '../../../../lib/slugify';
import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { Input } from '../../../../components/ui/input';
import { Card, CardContent } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import CmsContentEditor from '../../../../components/cms/CmsContentEditor';

interface EntityItem {
  id: number;
  name: string;
  code?: string | null;
  show_on_homepage?: boolean;
}

interface CmsEntityContentPageProps {
  /** 'standort' → /standort/{id} content, 'fach' → /fach/{id} content. */
  kind: 'standort' | 'fach';
}

const CONFIG = {
  standort: {
    endpoint: '/api/standorte',
    heading: 'Standorte',
    label: 'Standort',
    previewBase: '/standort',
    overviewSlug: 'standorte',
    overviewPreview: '/standorte',
    overviewTitle: 'Übersichtsseite (Hero)',
  },
  fach: {
    endpoint: '/api/faecher',
    heading: 'Fächer',
    label: 'Fach',
    previewBase: '/fach',
    overviewSlug: 'faecher',
    overviewPreview: '/faecher',
    overviewTitle: 'Übersichtsseite (Hero)',
  },
} as const;

const CmsEntityContentPage: React.FC<CmsEntityContentPageProps> = ({ kind }) => {
  // Die Bezeichnung für „Fach“ legt der Super-Admin zentral fest (ADM-19.2).
  const cfg = kind === 'fach'
    ? { ...CONFIG.fach, heading: getSubjectLabels().plural, label: getSubjectLabels().singular }
    : CONFIG[kind];
  const [items, setItems] = useState<EntityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<EntityItem | null>(null);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Include inactive entities so they stay manageable (and re-activatable)
      // here even when hidden from the website.
      const rows = await api.get<EntityItem[]>(`${cfg.endpoint}?active_only=false`);
      setItems(Array.isArray(rows) ? rows : []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `${cfg.heading} konnten nicht geladen werden`);
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint, cfg.heading]);

  useEffect(() => { void load(); }, [load]);

  const toggleActive = async (item: EntityItem, next: boolean) => {
    setTogglingId(item.id);
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, show_on_homepage: next } : i)));
    try {
      await api.patch(`${cfg.endpoint}/${item.id}`, { show_on_homepage: next });
    } catch (e) {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, show_on_homepage: !next } : i)));
      toast.error(e instanceof Error ? e.message : 'Sichtbarkeit konnte nicht geändert werden');
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = items.filter((i) =>
    i.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{cfg.heading}</h1>
          <p className="text-sm text-muted-foreground">
            Inhalte pro {cfg.label} pflegen und Anzeige auf der Startseite steuern.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => setOverviewOpen(true)}>
            <LayoutTemplate className="mr-2 h-4 w-4" /> Übersichtsseite
          </Button>
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {items.length === 0 ? `Keine ${cfg.heading} vorhanden.` : 'Keine Treffer.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {filtered.map((item) => {
              const active = item.show_on_homepage !== false;
              return (
                <div key={item.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{item.name}</div>
                    {!active && (
                      <div className="text-xs text-amber-600">Nicht auf der Startseite</div>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Switch
                      checked={active}
                      disabled={togglingId === item.id}
                      onCheckedChange={(v) => toggleActive(item, v)}
                      aria-label="Auf der Startseite anzeigen"
                    />
                    Auf Startseite
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditing(item)}>
                    <Pencil className="mr-2 h-4 w-4" /> Inhalte
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Dialog open={overviewOpen} onOpenChange={setOverviewOpen}>
        <DialogContent size="full" className="h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{cfg.heading}: Übersichtsseite</DialogTitle>
          </DialogHeader>
          <CmsContentEditor
            slug={cfg.overviewSlug}
            title={cfg.overviewTitle}
            description={`Hero-Bereich der ${cfg.heading}-Übersichtsseite (${cfg.overviewPreview}).`}
            previewPath={cfg.overviewPreview}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent size="full" className="h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `${cfg.label}: ${editing.name}` : ''}</DialogTitle>
          </DialogHeader>
          {editing && (
            <CmsContentEditor
              key={editing.id}
              slug={`${kind}/${editing.id}`}
              title={editing.name}
              description={`${cfg.label}-Inhalte`}
              previewPath={`${cfg.previewBase}/${slugify(editing.code || editing.name)}`}
              // Jobs werden eigenständig im Standort-Admin-Menü gepflegt.
              excludeKeys={kind === 'standort' ? ['jobs'] : undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsEntityContentPage;
